import {
  WHATSAPP_UNAVAILABLE_REASONS,
  type LeadCardResponse,
  type WHATSAPP_MESSAGE_STATUSES,
  type WhatsAppChannelCode,
  type WhatsAppTemplateView,
  type WhatsAppUnavailableReason,
} from "@gioia365/contracts";
import { DAILY_RECIPIENT_CAP, type WhatsAppFallbackReason } from "@gioia365/domain/whatsapp";
import type { MessageKey } from "@gioia365/i18n";
import { ApiRequestError } from "../api/http";
import { messageForError } from "../i18n/errors";
import { formatNumericTime } from "../i18n/format";
import type { Translate } from "../i18n/locale";

// Le parole del WhatsApp a mano che servono alla testata, alla finestra e alla postazione (piano M6, R1, Task 4).

/**
 * Perché «WhatsApp» è spento (`whatsapp.reason` della scheda, o il motivo di un rifiuto della preparazione). `no_mobile` ha due
 * frasi: il Lead ha un cellulare segnato errato (le parole del mock-up), oppure non ne ha nessuno.
 */
export function unavailableReasonText(t: Translate, card: LeadCardResponse, reason: WhatsAppUnavailableReason): string {
  switch (reason) {
    case "do_not_contact":
      return t("whatsapp.reasons.do_not_contact", { status: card.lead.status.name });
    case "no_mobile":
      return card.lead.phones.some((phone) => phone.kind === "mobile") ? t("whatsapp.reasons.wrong_number") : t("whatsapp.reasons.no_mobile");
    default:
      return t(`whatsapp.reasons.${reason}`);
  }
}

/** Il motivo che il server ha scritto in un rifiuto (`details.reason`), se è uno di quelli della scheda. */
function refusalReason(error: ApiRequestError): WhatsAppUnavailableReason | null {
  return WHATSAPP_UNAVAILABLE_REASONS.find((reason) => reason === error.details?.reason) ?? null;
}

/**
 * Il testo di un rifiuto della preparazione o dell'invio dal WhatsApp ufficiale (Task 9b): il motivo del Lead o di chi scrive, quando
 * il server lo dice, altrimenti il codice (`409 whatsapp_recipient_not_allowed`: «Numero non ammesso nell'azienda di prova…»).
 */
export function prepareRefusalText(t: Translate, card: LeadCardResponse, error: unknown): string {
  if (error instanceof ApiRequestError && (error.code === "whatsapp_blocked" || error.code === "permission_denied")) {
    const reason = refusalReason(error);
    if (reason) return unavailableReasonText(t, card, reason);
  }
  if (error instanceof ApiRequestError && error.code === "validation_failed" && Array.isArray(error.details?.missing) && error.details.missing.length > 0) {
    return t("whatsapp.dialog.missing", { fields: missingFields(error.details.missing.map(String)) });
  }
  return messageForError(t, error);
}

/** Le variabili che mancano come stanno nel testo: «{giorno e ora}», «{nome}, {giorno e ora}». */
export function missingFields(names: readonly string[]): string {
  return names.map((name) => `{${name}}`).join(", ");
}

/**
 * Perché il messaggio si apre a mano invece di partire dal WhatsApp ufficiale (piano M6, R2, Task 9b; D2: il ripiego si dice sempre),
 * per un motivo di `whatsappRoute`: le frasi di `waRoute` del mock-up (`mockup.js`) alla lettera, e per il canale spento e il
 * modello con un campo vuoto le proposte nello stesso stile (questione 409). Chi non ha un WhatsApp scrive a mano senza un perché,
 * come nel mock-up: `null`. `channel` è il WhatsApp di chi scrive (WhatsApp Agile ha le sue frasi). Con il ritmo di WhatsApp Agile
 * (R9, Task 27b) l'ora del prossimo invio (`nextAt`, dalla risposta del server) si dice nel fuso dell'azienda (`timeZone`).
 */
export function fallbackReasonText(
  t: Translate,
  reason: WhatsAppFallbackReason,
  facts: { channel: WhatsAppChannelCode | null; template: Pick<WhatsAppTemplateView, "name" | "metaStatus"> | null; nextAt?: string | null; timeZone?: string },
): string | null {
  const agile = facts.channel === "agile";
  switch (reason) {
    case "no_sender":
      return null;
    case "channel_disabled":
      return t(agile ? "whatsapp.dialog.fallback.channelDisabledAgile" : "whatsapp.dialog.fallback.channelDisabledMeta");
    case "sender_not_ready":
      return t(agile ? "whatsapp.dialog.fallback.senderNotReadyAgile" : "whatsapp.dialog.fallback.senderNotReadyMeta");
    case "template_not_approved": {
      if (facts.template === null) return t("whatsapp.dialog.fallback.notReady");
      // Un modello che il browser crede approvato e il server no ha cambiato testo nel frattempo: torna «da inviare» (ruling A3b).
      const status = facts.template.metaStatus === null || facts.template.metaStatus === "approved" ? "none" : facts.template.metaStatus;
      return t("whatsapp.dialog.fallback.templateNotApproved", { template: facts.template.name, status: t(`whatsapp.dialog.metaStatus.${status}`) });
    }
    case "missing_values":
      return facts.template === null ? t("whatsapp.dialog.fallback.notReady") : t("whatsapp.dialog.fallback.missingValues", { template: facts.template.name });
    // I controlli di WhatsApp Agile al momento dell'invio (piano M6, R9).
    case "recipient_not_allowed":
      return t("whatsapp.dialog.fallback.recipientNotAllowed");
    case "daily_cap":
      return t("whatsapp.dialog.fallback.dailyCap", { cap: DAILY_RECIPIENT_CAP });
    case "paced":
      return facts.nextAt && facts.timeZone
        ? t("whatsapp.dialog.fallback.pacedAt", { time: formatNumericTime(facts.timeZone, facts.nextAt) })
        : t("whatsapp.dialog.fallback.paced");
    case "channel_down":
      return t("whatsapp.dialog.fallback.channelDown");
    case "provider_unreachable":
      return t("whatsapp.dialog.fallback.providerUnreachable");
  }
}

/**
 * Il tetto di WhatsApp Agile detto dal server (`409 whatsapp_daily_cap`, WA-10; piano M6, R9, Task 27b): con il conteggio di oggi quando
 * il server l'ha letto (`details.todayCount`), altrimenti soltanto il tetto. `null` per un altro errore.
 */
export function dailyCapRefusalOf(error: unknown): { count: number | null } | null {
  if (!(error instanceof ApiRequestError) || error.code !== "whatsapp_daily_cap") return null;
  const count = error.details?.todayCount;
  return { count: typeof count === "number" && Number.isInteger(count) && count >= 0 ? count : null };
}

/** Il testo del tetto di WhatsApp Agile detto dal server: con il conteggio, o con il solo tetto. */
export function dailyCapText(t: Translate, count: number | null): string {
  return count === null
    ? t("whatsapp.dialog.fallback.dailyCap", { cap: DAILY_RECIPIENT_CAP })
    : t("whatsapp.dialog.fallback.dailyCapCount", { count, cap: DAILY_RECIPIENT_CAP });
}

type WhatsAppMessageStatusCode = (typeof WHATSAPP_MESSAGE_STATUSES)[number];

/** Il codice di un messaggio la cui risposta del fornitore non è arrivata: può essere partito (`WHATSAPP_PROVIDER_NO_RESPONSE`). */
const NO_RESPONSE = "no_response";
/** Il codice di un messaggio preso in carico che il fornitore non ha mai confermato (il giro di ogni ora). */
const NOT_CONFIRMED = "not_confirmed";

/**
 * Lo stato di un messaggio di WhatsApp Agile in uscita (piano M6, R9, Task 27b; WA-7: Agile sa soltanto «preso in carico»), nella
 * Timeline e in «Conversazioni → WhatsApp»: in invio, «Preso in carico da WhatsApp Agile», non preso, senza risposta (non sappiamo se è
 * partito), mai confermato. `null` per gli altri stati, che Agile non ha.
 */
export function agileStateKey(status: WhatsAppMessageStatusCode, failureCode: string | null): Extract<MessageKey, `timeline.whatsapp.agileStates.${string}`> | null {
  switch (status) {
    case "queued":
      return "timeline.whatsapp.agileStates.queued";
    case "sent":
      return "timeline.whatsapp.agileStates.sent";
    case "failed":
      if (failureCode === NO_RESPONSE) return "timeline.whatsapp.agileStates.noResponse";
      return failureCode === NOT_CONFIRMED ? "timeline.whatsapp.agileStates.notConfirmed" : "timeline.whatsapp.agileStates.failed";
    default:
      return null;
  }
}

/**
 * Il motivo di un messaggio di WhatsApp Agile non preso (R9, Task 27b), nella Timeline: la risposta d'errore del fornitore con il suo
 * testo (`http_<stato>`), un numero che non ha la forma che il fornitore accetta, oppure, senza risposta o mai confermato, che non si
 * rimanda da sé (può essere partito). `null` senza un codice.
 */
export function agileFailureText(t: Translate, failureCode: string | null, failureReason: string | null): string | null {
  if (failureCode === null) return null;
  if (failureCode === NO_RESPONSE || failureCode === NOT_CONFIRMED) return t("timeline.whatsapp.agileFailures.notResent");
  let reason: string;
  const status = /^http_(\d{3})$/.exec(failureCode)?.[1];
  if (failureCode === "invalid_reference") reason = t("timeline.whatsapp.agileFailures.invalidReference");
  else if (status !== undefined) reason = failureReason ? t("timeline.whatsapp.agileFailures.httpWithText", { text: failureReason, status }) : t("timeline.whatsapp.agileFailures.http", { status });
  else reason = failureReason ? t("timeline.whatsapp.failureWithCode", { title: failureReason, code: failureCode }) : t("timeline.whatsapp.failureCode", { code: failureCode });
  return t("timeline.whatsapp.failureReason", { reason });
}
